import React, { useState, useEffect, useCallback } from "react";
import { FaCalendarAlt } from "react-icons/fa";
import API_BASE from "./apiConfig";
import { Colors } from "./branding";
import { useTheme, getT, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import { PrimaryButton, GhostButton } from "./components/ThemedButton";
import StatusBadge from "./components/StatusBadge";
import { useSort } from "./hooks/useSort";
import { useDateTimeFormat } from "./utils/formatters";

const C = { get yellow() { return getBrandColors().yellow; }, get blue() { return getBrandColors().blue; } };
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const STATUS_META = {
  pending_payment: { label: "Pending Payment", tone: "warning" },
  confirmed:       { label: "Confirmed",       tone: "success" },
  checked_in:      { label: "Checked In",      tone: "info" },
  completed:       { label: "Completed",        tone: "info" },
  cancelled:       { label: "Cancelled",        tone: "neutral" },
  no_show:         { label: "No Show",          tone: "warning" },
};

const card = (T) => ({
  background: T ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.90)",
  border: T ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(3,78,123,0.12)",
  borderRadius: 14, padding: "18px 22px", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
});
const kpiCard = (T) => ({ ...card(T), textAlign: "center", padding: "16px 20px" });

export default function OTOCoachView({ currentUser }) {
  const { isDark: T } = useTheme();
  const Theme = getT(T);
  const { fmtDate, fmtTime } = useDateTimeFormat();
  const textPrimary   = T ? "rgba(255,255,255,0.88)" : "#1a2a3a";
  const textSecondary = T ? "rgba(255,255,255,0.50)" : "rgba(3,78,123,0.55)";
  const divider       = T ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.08)";

  const now   = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year,  setYear]  = useState(now.getFullYear());
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [msgMap,   setMsgMap]   = useState({});

  const coachId  = currentUser?.user_id;
  const isAdmin  = currentUser?.user_role === "Admin" || currentUser?.is_superadmin;

  const load = useCallback(async () => {
    if (!coachId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/oto/bookings?coach_id=${coachId}&month=${month}&year=${year}`);
      const d   = await res.json();
      setBookings(d.bookings || []);
    } catch { setBookings([]); }
    setLoading(false);
  }, [coachId, month, year]);

  useEffect(() => { load(); }, [load]);

  const { sorted: sortedBookings, toggleSort: toggleBookingSort, sortArrow: bookingSortArrow } = useSort(bookings, "session_date");

  const checkin = async (bookingId) => {
    setMsgMap(p => ({ ...p, [bookingId]: { loading: true } }));
    try {
      const res = await fetch(`${API_BASE}/oto/bookings/${bookingId}/checkin`, { method: "POST" });
      const d   = await res.json();
      setMsgMap(p => ({ ...p, [bookingId]: { ok: d.success, text: d.message } }));
      if (d.success) load();
    } catch {
      setMsgMap(p => ({ ...p, [bookingId]: { ok: false, text: "Network error" } }));
    }
  };

  const complete = async (bookingId) => {
    await fetch(`${API_BASE}/oto/bookings/${bookingId}/complete`, { method: "POST" });
    load();
  };

  // Summary stats
  const confirmed  = bookings.filter(b => ["confirmed","checked_in","completed"].includes(b.status));
  const totalMins  = confirmed.reduce((s, b) => s + (Number(b.duration_minutes) || 0), 0);
  const completed  = bookings.filter(b => b.status === "completed").length;
  const checkedIn  = bookings.filter(b => b.status === "checked_in").length;

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: textPrimary, padding: "28px 32px", maxWidth: 1100 }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <PageHeaderIcon icon={<FaCalendarAlt size={20} color={getBrandColors().blueDark} />} />
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T ? "#fff" : C.blue }}>My 1-on-1 Schedule</div>
            <div style={{ fontSize: 13, color: textSecondary, marginTop: 3 }}>
              Check in students after confirming payment is received.
            </div>
          </div>
        </div>
        {/* Month / Year selector */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={month} onChange={e => setMonth(Number(e.target.value))}
            style={{ padding: "7px 11px", borderRadius: 8, fontSize: 13, border: `1px solid ${T ? "rgba(255,255,255,0.14)" : "rgba(3,78,123,0.18)"}`,
              background: T ? "rgba(255,255,255,0.08)" : "#f4f7fb", color: textPrimary, outline: "none" }}>
            {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            style={{ padding: "7px 11px", borderRadius: 8, fontSize: 13, border: `1px solid ${T ? "rgba(255,255,255,0.14)" : "rgba(3,78,123,0.18)"}`,
              background: T ? "rgba(255,255,255,0.08)" : "#f4f7fb", color: textPrimary, outline: "none" }}>
            {[2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <GhostButton size="sm" onClick={load}>Refresh</GhostButton>
        </div>
      </div>

      {/* KPI Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 24 }}>
        {[
          { label: "Total Bookings",  value: bookings.length },
          { label: "Confirmed",       value: confirmed.length, highlight: true },
          { label: "Checked In",      value: checkedIn },
          { label: "Hours Locked",    value: `${Math.floor(totalMins/60)}h ${totalMins%60}m` },
        ].map((k, i) => (
          <div key={i} style={kpiCard(T)}>
            <div style={{ fontSize: 24, fontWeight: 800, color: k.highlight ? C.yellow : textPrimary }}>{k.value}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: textSecondary, marginTop: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Sessions Table */}
      <div style={card(T)}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>
          {MONTHS[month-1]} {year} — Sessions ({bookings.length})
        </div>

        {loading ? (
          <div style={{ color: textSecondary, padding: "24px 0", textAlign: "center" }}>Loading…</div>
        ) : bookings.length === 0 ? (
          <div style={{ color: textSecondary, padding: "40px 0", textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📅</div>
            <div>No sessions booked for {MONTHS[month-1]} {year}</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {[
                    { label: "Date", key: "session_date" },
                    { label: "Time", key: "start_time" },
                    { label: "Student", key: "first_name" },
                    { label: "Duration", key: "duration_minutes" },
                    ...(isAdmin ? [{ label: "Amount", key: "amount_due" }] : []),
                    { label: "Status", key: "status" },
                    { label: "Action", key: null },
                  ].map(h => (
                    <th key={h.label} onClick={h.key ? () => toggleBookingSort(h.key) : undefined}
                      style={{ padding: "9px 12px", textAlign: "left", fontSize: 11, fontWeight: 700,
                      letterSpacing: "0.06em", textTransform: "uppercase", color: textSecondary,
                      borderBottom: `2px solid ${divider}`, whiteSpace: "nowrap",
                      ...(h.key ? { cursor: "pointer", userSelect: "none" } : {}) }}>
                      {h.label}{h.key ? bookingSortArrow(h.key) : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedBookings.map((b, ri) => {
                  const meta = STATUS_META[b.status] || { label: b.status, tone: "neutral" };
                  const msg  = msgMap[b.booking_id];
                  return (
                    <tr key={b.booking_id} style={{ background: ri % 2 === 0 ? "transparent" : (T ? "rgba(255,255,255,0.02)" : "rgba(3,78,123,0.02)") }}>
                      <td style={{ padding: "10px 12px", borderBottom: `1px solid ${divider}`, fontWeight: 600 }}>{fmtDate(b.session_date)}</td>
                      <td style={{ padding: "10px 12px", borderBottom: `1px solid ${divider}`, whiteSpace: "nowrap" }}>
                        {fmtTime(b.start_time)} – {fmtTime(b.end_time)}
                      </td>
                      <td style={{ padding: "10px 12px", borderBottom: `1px solid ${divider}`, fontWeight: 600 }}>
                        {b.first_name} {b.last_name}
                      </td>
                      <td style={{ padding: "10px 12px", borderBottom: `1px solid ${divider}` }}>
                        {b.duration_minutes} min
                      </td>
                      {isAdmin && (
                        <td style={{ padding: "10px 12px", borderBottom: `1px solid ${divider}`, fontWeight: 700, color: C.blue }}>
                          ${Number(b.amount_due || 0).toFixed(2)}
                        </td>
                      )}
                      <td style={{ padding: "10px 12px", borderBottom: `1px solid ${divider}` }}>
                        <StatusBadge label={meta.label} tone={meta.tone} />
                      </td>
                      <td style={{ padding: "10px 12px", borderBottom: `1px solid ${divider}` }}>
                        {msg?.loading ? (
                          <span style={{ fontSize: 12, color: textSecondary }}>…</span>
                        ) : msg?.text ? (
                          <span style={{ fontSize: 12, color: msg.ok ? getBrandColors().green : getBrandColors().red, fontWeight: 600 }}>{msg.text}</span>
                        ) : b.status === "confirmed" ? (
                          <PrimaryButton size="sm" onClick={() => checkin(b.booking_id)}>✓ Check In</PrimaryButton>
                        ) : b.status === "checked_in" ? (
                          <GhostButton size="sm" style={{ background: "#7c3aed" }} onClick={() => complete(b.booking_id)}>Mark Complete</GhostButton>
                        ) : b.status === "pending_payment" ? (
                          <span style={{ fontSize: 11, color: textSecondary }}>Awaiting payment</span>
                        ) : (
                          <span style={{ fontSize: 11, color: textSecondary }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Revenue leakage notice */}
      <div style={{ marginTop: 16, padding: "12px 16px", borderRadius: 10,
        background: T ? "rgba(247,206,11,0.08)" : "rgba(247,206,11,0.12)",
        border: `1px solid rgba(247,206,11,0.30)`, fontSize: 12, color: T ? "rgba(255,255,255,0.65)" : "#92610a" }}>
        ⚠️  <strong>Check-in is only available for Confirmed sessions</strong> (payment received). If a booking shows "Pending Payment", ask the parent or admin to complete payment first.
      </div>
    </div>
  );
}
